import { Box, Button, ButtonVariant, Col, Container, Dropdown, DropdownField, ErrorText, Flex, Footnote, Link, Row, thc } from "@flightstats/airframe-react";
import { AddToast } from "../../../types/airframe";
import { SaveProfileError, TravelerProfileContext } from "../TravelerProfileFormHandler";
import { PRIVACY_POLICY_URL } from "../../../constants";
import { contactPointFormIds, ContactPointsAlertType, ContactPointsMessageType, ContactPointsMessageTypeOptions } from "../TravelerProfileConstants";
import { ContactPoint, TravelerProfile } from "../../../types/travelerProfile";
import React, { useContext, useState } from "react";
import OptInState, { checkSearchResponseAndUpdate } from "../OptInState";
import _ from "lodash";
import { AlertSwitch } from "./AccordionAlertSwitch";
import { isIe11 } from "../../../service/commonFetch";
import { ContactPointsPhoneField, preferredCountryCode, preferredPhoneInput } from "./ContactPointsPhoneField";
import { ContactPointsEmailField } from "./ContactPointsEmailField";

type ContactPointsRowProps = {
    contactPoint: ContactPoint;
    disabled: boolean;
    onTestAlert: (addToast: AddToast, contactPoint: ContactPoint) => void;
    supportedSMSCountryCodes: string[];
    isDisabled: boolean;
    setIsDisabled: (isDisabled: boolean) => void;
    saveProfilePhoneError?: SaveProfileError;
    setSaveProfilePhoneError: (error: SaveProfileError) => void;
};

const ContactPointsWrapper = thc(Box, {
    width: '100%',
});

const TestAlertButtonWrapper = thc(Box, {
    width: '100%',
    mb: {
        xs: '20px',
        sm: '0',
    },
    mt: {
        xs: '5px',
        sm: '24px',
    },
    textAlign: {
        xs: 'center',
        sm: 'right',
    },
});

const SMSContextMessage: React.FC = () => (
    <Row>
        <Col xs={12} sm={9}>
            <Footnote>
                Message and data rates may apply. Message frequency varies. Text STOP to
                opt out or HELP for help. See our{' '}
                <Link target="_blank" href="/traveler/terms">
                    Terms and Conditions
                </Link>{' '}
                and{' '}
                <Link target="_blank" href={PRIVACY_POLICY_URL}>
                    Privacy Policy
                </Link>
                .
            </Footnote>
        </Col>
    </Row>
);

export const ContactPointsRow: React.FC<ContactPointsRowProps> = ({
    contactPoint,
    disabled,
    onTestAlert,
    supportedSMSCountryCodes,
    isDisabled,
    setIsDisabled,
    saveProfilePhoneError,
    setSaveProfilePhoneError,
}) => {
    const contactEmail =
        contactPoint.messageType === ContactPointsMessageType.EMAIL
            ? contactPoint.contact
            : undefined;
    const phoneNumber = contactPoint.messageType === ContactPointsMessageType.SMS ? contactPoint.phoneNumberE164 : undefined;

    const {
        alertType,
        messageType,
        countryCode,
        optInState,
        setOptInState,
    } = contactPoint;

    const initialMessageTypeValue =
        ContactPointsMessageTypeOptions.find(
            option => option.value === messageType,
        ) || ContactPointsMessageTypeOptions[0];

    const [messageTypeValue, updateMessageTypeValue] = useState(
        initialMessageTypeValue,
    );

    const [contactEmailInput, updateContactEmailInput] = useState(contactEmail);
    const [isEmailEmpty, setIsEmailEmpty] = useState(_.isEmpty(contactEmail));
    const [initialPhoneNumber, updateInitialPhoneNumber] = useState(phoneNumber);
    const [phoneNumberInput, updatePhoneNumberInput] = useState(phoneNumber);
    const [initialCountryCode, updateInitialCountryCode] = useState(countryCode);
    const [countryCodeInput, updateCountryCodeInput] = useState(countryCode);
    const [isEmailInvalid, setIsEmailInvalid] = useState(false);
    const [phoneValidationErrorText, setPhoneValidationErrorText] = useState('');

    const [isEmail, updateIsEmail] = useState(
        messageType === ContactPointsMessageType.EMAIL,
    );

    const { doSendVerificationSms, addToast, setTravelerProfile } = useContext(
        TravelerProfileContext,
    );

    const onVerificationStateSend = (resend: boolean) => {
        //attempt to send the SMS
        if (phoneNumberInput && countryCodeInput && doSendVerificationSms) {
            return doSendVerificationSms(phoneNumberInput, countryCodeInput, resend)
                .then(response => {
                    if (response && response.search && setOptInState && addToast) {
                        checkSearchResponseAndUpdate(
                            setOptInState,
                            addToast,
                            response.search,
                        );
                    } else {
                        addToast?.(
                            `Error in ${resend ? 'Re' : ''}sending Opt-in sms response`,
                            { variant: 'error' },
                        );
                        console.error(
                            `${resend ? 'Re' : ''
                            }Send's response is missing or is missing 'search' property: ${JSON.stringify(
                                response || {},
                            )}`,
                        );
                    }
                })
                .catch(err => {
                    addToast?.(`Error ${resend ? 'Re' : ''}sending Opt-in sms`, {
                        variant: 'error',
                    });
                    console.error(
                        `${resend ? 'Re' : ''
                        }Sending Opt-in sms failed on: ${JSON.stringify(err)}`,
                    );
                });
        } else {
            //to appease flow when phoneNumberInput or countryCodeInput aren't defined, but still return a Promise<void>
            return new Promise<void>(res => {
                res();
            });
        }
    };

    const onVerificationStateTest = () => {
        if (phoneNumberInput && countryCodeInput && addToast) {
            return onTestAlert(addToast, getCurrentContactPoint());
        }
    };

    const isNotSupportingSms = (messageTypeValue: { value: string }) =>
        supportedSMSCountryCodes.length === 0 &&
        ContactPointsMessageType.SMS === messageTypeValue.value;

    const isNotSupportingSmsHelpText = (messageTypeValue: { value: string }) =>
        isNotSupportingSms(messageTypeValue) ? 'Not available' : undefined;

    const hasNoMessageTypeSelection =
        alertType === ContactPointsAlertType.HEALTH_AND_SAFETY;

    const getCurrentContactPoint = (overrides: Partial<ContactPoint> = {}) => {
        const localIsEmail = overrides.messageType
            ? overrides.messageType === ContactPointsMessageType.EMAIL
            : messageType === ContactPointsMessageType.EMAIL;

        const currentContactPoint: ContactPoint = {
            ...contactPoint, //to avoid existing properties we don't care about here being removed.
            alertType,
            messageType: messageTypeValue ? messageTypeValue.value : messageType,
            contact: localIsEmail ? contactEmailInput : phoneNumberInput,
            format: localIsEmail ? 'HTML' : 'MOBILE',
            isDisabled: isDisabled,
            countryCode: localIsEmail
                ? undefined
                : countryCodeInput || preferredCountryCode,
            phoneNumberE164: localIsEmail
                ? undefined
                : phoneNumberInput || preferredPhoneInput,
        };
        return Object.assign(currentContactPoint, overrides);
    };

    const updateContactPoint = (overrides: Partial<ContactPoint> = {}) => {
        const currentContactPoint = getCurrentContactPoint(overrides);

        setTravelerProfile?.(newTravelerProfile => {
            const contactPoints = newTravelerProfile.contactPoints.map(
                contactPoint => {
                    if (contactPoint.alertType === alertType) {
                        return currentContactPoint;
                    } else {
                        return contactPoint;
                    }
                },
            );

            return { ...newTravelerProfile, contactPoints };
        });
    };

    const MessageTypeSelection = () => (
        <DropdownField
            validate={(value: { value: string; }) => isNotSupportingSmsHelpText(value)}
        >
            <Dropdown
                label="Message Type"
                id={contactPointFormIds(alertType).messageType}
                disabled={disabled}
                options={ContactPointsMessageTypeOptions}
                value={messageTypeValue}
                onChange={(selection: any) => {
                    if (messageTypeValue !== selection) {
                        const localIsEmail =
                            selection.value === ContactPointsMessageType.EMAIL;
                        updateMessageTypeValue(selection);
                        updateIsEmail(localIsEmail);
                        updateInitialCountryCode(countryCodeInput);
                        updateInitialPhoneNumber(phoneNumberInput);

                        updateContactPoint({
                            //all these overrides are to bypass the useState update race from all of the above
                            messageType: selection.value,
                        });
                    }
                }}
                error={isNotSupportingSms(messageTypeValue)}
                helperText={isNotSupportingSmsHelpText(messageTypeValue)}
            />
        </DropdownField>
    );

    return (
        <Container fluid style={{ paddingTop: '10px', paddingBottom: '10px' }}>
            <Row>
                {isIe11() && (
                    <Col xs={12} align="flex-end">
                        <AlertSwitch
                            isDisabled={isDisabled}
                            setIsDisabled={setIsDisabled}
                            alertType={alertType}
                        />
                    </Col>
                )}

            {hasNoMessageTypeSelection ? (
              <Box display="none" disabled={isDisabled}>
               <MessageTypeSelection />
              </Box>
            ) : (
              <Col xs={8} sm={1} disabled={isDisabled}>
               <MessageTypeSelection />
              </Col>
            )}

                <Col xs={8} sm={hasNoMessageTypeSelection ? 6 : 2}>
                    <ContactPointsWrapper>
                        {isEmail ? (
                            <ContactPointsEmailField
                                disabled={disabled}
                                id={contactPointFormIds(alertType).email}
                                value={contactEmail || ''}
                                updateContactEmailInput={updateContactEmailInput}
                                setIsEmailEmpty={setIsEmailEmpty}
                                setIsEmailInvalid={setIsEmailInvalid}
                            />
                        ) : (
                            <ContactPointsPhoneField
                                saveProfilePhoneError={saveProfilePhoneError}
                                setSaveProfilePhoneError={setSaveProfilePhoneError}
                                disabled={disabled || isNotSupportingSms(messageTypeValue)}
                                id={contactPointFormIds(alertType).phone}
                                alertType={alertType}
                                countryCode={initialCountryCode}
                                phoneNumber={initialPhoneNumber || ''}
                                updatePhoneNumberInput={updatePhoneNumberInput}
                                updateCountryCodeInput={updateCountryCodeInput}
                                supportedSMSCountryCodes={supportedSMSCountryCodes}
                                phoneValidationErrorText={phoneValidationErrorText}
                                setPhoneValidationErrorText={setPhoneValidationErrorText}
                                setOptInState={setOptInState}
                                updateContactPoint={updateContactPoint}
                            />
                        )}
                    </ContactPointsWrapper>
                </Col>
                <Col xs={8} sm={1}>
                    {isEmail ? (
                        <TestAlertButtonWrapper>
                            <Button
                                variant={ButtonVariant.Secondary}
                                disabled={
                                    disabled ||
                                    isNotSupportingSms(messageTypeValue) ||
                                    (isEmail ? isEmailEmpty : !!phoneValidationErrorText) ||
                                    isEmailInvalid
                                }
                                onClick={() => addToast && onTestAlert(addToast, getCurrentContactPoint())}
                            >
                                TEST ALERT
                            </Button>
                        </TestAlertButtonWrapper>
                    ) : (
                        optInState && (
                            <div>
                                <OptInState
                                    state={optInState}
                                    disabled={disabled}
                                    onSend={() => {
                                        setIsDisabled(true);
                                        onVerificationStateSend(false)
                                            .then(() => {
                                                setIsDisabled(false);
                                            })
                                            .catch(console.error);
                                    }}
                                    onResend={() => {
                                        onVerificationStateSend(true);
                                    }}
                                    onTest={onVerificationStateTest}
                                />
                            </div>
                        )
                    )}
                </Col>
                <Col xs={8}>
                    {isNotSupportingSms(messageTypeValue) && (
                        <Flex>
                            <ErrorText
                                idPrefix=""
                                icon={false}
                                helperText="SMS Messaging is not available for this account. Please contact your travel provider for more information."
                            />
                        </Flex>
                    )}
                </Col>
            </Row>
            <Row>
                <Col xs={8}>
                    {!isNotSupportingSms(messageTypeValue) && !isEmail && (
                        <SMSContextMessage />
                    )}
                </Col>
            </Row>
        </Container>
    );
};